import React, { useState, useEffect, useRef } from 'react';
import {
    FileText, Printer, Search, RefreshCw,
    Loader2, AlertCircle, X, Building2,
    ChevronLeft, ChevronRight, Plus, FilePlus,
} from 'lucide-react';
import API_BASE_URL from "../config";
import { jsPDF } from 'jspdf';

export default function Gatepass() {
    const [activeTab, setActiveTab] = useState('list'); // 'list' | 'create'

    // ── LIST TAB STATE ──
    const [branchMRs, setBranchMRs] = useState([]);
    const [filteredMRs, setFilteredMRs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [selectedMR, setSelectedMR] = useState(null);
    const [selectedMRDetails, setSelectedMRDetails] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showGatepass, setShowGatepass] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const printRef = useRef();

    // ── MANUAL CREATE TAB STATE ──
    const [manualForm, setManualForm] = useState({
        customerName: '',
        customerAddress: '',
        wbRef: '',
        deliveryDate: new Date().toISOString().split('T')[0],
        expectedArrivalDate: '',
        truckPlateNo: '',
        requestedBy: '',
        preparedBy: '',
        verifiedBy: '',
        dispatchBy: '',
        items: [
            { code: '', description: '', remarks: '', qty: '', packaging: '' }
        ]
    });
    const [trucks, setTrucks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loadingFormData, setLoadingFormData] = useState(false);

    const statusLabels = {
        0: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
        1: { label: 'For Approval', color: 'bg-yellow-100 text-yellow-700' },
        2: { label: 'Approved', color: 'bg-green-100 text-green-700' },
        3: { label: 'Released', color: 'bg-blue-100 text-blue-700' },
        4: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
    };

    useEffect(() => { fetchBranchMRs(); fetchFormData(); }, []);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                setManualForm(prev => ({ ...prev, requestedBy: fullName, preparedBy: fullName }));
            } catch {}
        }
    }, []);

    useEffect(() => {
        const filtered = branchMRs.filter(mr => {
            const search = searchText.toLowerCase();
            return (
                mr.mrNo?.toLowerCase().includes(search) ||
                mr.controlNo?.toLowerCase().includes(search) ||
                mr.giNumber?.toLowerCase().includes(search) ||
                mr.gi_Number?.toLowerCase().includes(search) ||
                mr.requestedByName?.toLowerCase().includes(search) ||
                mr.branchSpecify?.toLowerCase().includes(search) ||
                mr.departmentName?.toLowerCase().includes(search)
            );
        });
        setFilteredMRs(filtered);
        setCurrentPage(1);
    }, [searchText, branchMRs]);

    const fetchBranchMRs = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}api/MRHeader`);
            if (!res.ok) throw new Error('Failed to fetch MR headers');
            const data = await res.json();
            const branchOnly = data.filter(mr =>
                mr.department === 'Branch' || mr.departmentName?.toLowerCase().includes('branch')
            );
            setBranchMRs(branchOnly);
            setFilteredMRs(branchOnly);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchFormData = async () => {
        setLoadingFormData(true);
        try {
            const [trucksRes, usersRes] = await Promise.all([
                fetch(`${API_BASE_URL}api/Truck`),
                fetch(`${API_BASE_URL}api/Users`)
            ]);
            if (trucksRes.ok) {
                const data = await trucksRes.json();
                setTrucks(data.map(t => ({
                    id: t.id,
                    truckPlate: t.truckPlate || t.TruckPlate,
                    model: t.model || t.Model,
                })));
            }
            if (usersRes.ok) {
                const data = await usersRes.json();
                setUsers(data.filter(u => u.isActive === 1));
            }
        } catch (err) {
            console.error('Failed to fetch form data:', err);
        } finally {
            setLoadingFormData(false);
        }
    };

    // Helper: get GI Number from MR object (handles both camelCase variants)
    const getGINumber = (mr) => mr?.giNumber || mr?.gi_Number || mr?.GI_Number || '—';

    const handleViewGatepass = async (mr) => {
        setSelectedMR(mr);
        setLoadingDetails(true);
        setShowGatepass(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);
            if (!res.ok) throw new Error('Failed to fetch details');
            const details = await res.json();
            setSelectedMRDetails(details);
        } catch {
            setSelectedMRDetails([]);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handlePrint = () => {
        const printContents = printRef.current.innerHTML;
        const originalBody = document.body.innerHTML;
        document.body.innerHTML = `
            <html><head><style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: Arial, sans-serif; font-size: 11px; background: #fff; }
                table { border-collapse: collapse; width: 100%; }
                td, th { border: 1px solid #000; padding: 4px 6px; }
                @media print { @page { margin: 10mm; size: A4; } }
            </style></head><body>${printContents}</body></html>
        `;
        window.print();
        document.body.innerHTML = originalBody;
        window.location.reload();
    };

    // ── MANUAL FORM HELPERS ──
    const handleManualChange = (e) => {
        const { name, value } = e.target;
        setManualForm(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setManualForm(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], [field]: value };
            return { ...prev, items };
        });
    };

    const addItem = () => {
        setManualForm(prev => ({
            ...prev,
            items: [...prev.items, { code: '', description: '', remarks: '', qty: '', packaging: '' }]
        }));
    };

    const removeItem = (index) => {
        setManualForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleTruckSelect = (e) => {
        const truck = trucks.find(t => t.id === parseInt(e.target.value));
        setManualForm(prev => ({ ...prev, truckPlateNo: truck ? truck.truckPlate : '' }));
    };

    // ── EXPORT TO PDF (Manual) ──
    const exportManualToPDF = () => {
        const doc = new jsPDF({ unit: 'mm', format: 'a4' });
        const pageW = 210;
        let y = 15;

        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text('MEGA SOFT HYGIENIC PRODUCT INC.', pageW / 2, y, { align: 'center' });
        y += 6;
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text('38 - E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City', pageW / 2, y, { align: 'center' });
        y += 5;
        doc.text('Telefax : 984-10-01', pageW / 2, y, { align: 'center' });
        y += 6;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('GATEPASS', pageW / 2, y, { align: 'center' });
        y += 4;

        doc.setLineWidth(0.5);
        doc.line(14, y, 196, y);
        y += 7;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('DELIVERY TO', 14, y);
        y += 5;

        doc.setFont(undefined, 'bold');
        doc.setFontSize(8);
        doc.text('CUSTOMER NAME', 14, y);
        doc.text(':', 44, y);
        doc.setFont(undefined, 'normal');
        doc.text(manualForm.customerName || '', 47, y);
        doc.line(47, y + 1, 130, y + 1);
        doc.setFont(undefined, 'bold');
        doc.text('WBREF:', 140, y);
        doc.setFont(undefined, 'normal');
        doc.text(manualForm.wbRef || '', 157, y);
        doc.line(157, y + 1, 196, y + 1);
        y += 7;

        doc.setFont(undefined, 'bold');
        doc.text('CUSTOMER ADDRESS', 14, y);
        doc.text(':', 44, y);
        doc.setFont(undefined, 'normal');
        doc.text(manualForm.customerAddress || '', 47, y);
        doc.line(47, y + 1, 196, y + 1);
        y += 9;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('DELIVERY INSTRUCTION', 14, y);
        y += 5;

        const dlRows = [
            ['DELIVERY DATE', manualForm.deliveryDate],
            ['EXPECTED ARRIVAL DATE', manualForm.expectedArrivalDate],
            ['TRUCK PLATE NO.', manualForm.truckPlateNo],
        ];
        dlRows.forEach(([label, val]) => {
            doc.setFontSize(8);
            doc.setFont(undefined, 'bold');
            doc.text(label, 14, y);
            doc.setFont(undefined, 'normal');
            doc.text(val || '', 70, y);
            doc.line(70, y + 1, 130, y + 1);
            y += 6;
        });

        y += 2;
        doc.setFont(undefined, 'bold');
        doc.setFontSize(8);
        doc.text('Request by:', 14, y);
        doc.setFont(undefined, 'normal');
        doc.text(manualForm.requestedBy || '', 38, y);
        doc.line(38, y + 1, 130, y + 1);
        y += 10;

        const colWidths = [20, 68, 40, 20, 34];
        const headers = ['CODE', 'ITEM DESCRIPTION', 'REMARKS', 'QTY', 'PACKAGING'];
        const tableX = 14;
        const tableW = colWidths.reduce((a, b) => a + b, 0);

        doc.setFillColor(30, 64, 175);
        doc.rect(tableX, y, tableW, 7, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        doc.setFontSize(8);
        let cx = tableX;
        headers.forEach((h, i) => {
            doc.text(h, cx + colWidths[i] / 2, y + 5, { align: 'center' });
            cx += colWidths[i];
        });
        doc.setTextColor(0, 0, 0);
        y += 7;

        const rowH = 7;
        const allItems = [
            ...manualForm.items,
            ...Array.from({ length: Math.max(0, 5 - manualForm.items.length) }).map(() => ({
                code: '', description: '', remarks: '', qty: '', packaging: ''
            }))
        ];

        allItems.forEach((item) => {
            cx = tableX;
            doc.setFont(undefined, 'normal');
            doc.setFontSize(8);
            const cells = [item.code, item.description, item.remarks, item.qty, item.packaging];
            cells.forEach((val, i) => {
                doc.rect(cx, y, colWidths[i], rowH);
                doc.text(String(val || ''), cx + 2, y + 5);
                cx += colWidths[i];
            });
            y += rowH;
        });

        y += 10;

        const sigBoxW = 58;
        const sigLabels = ['Prepared by:', 'Verified By:', 'Dispatch by:'];
        const sigValues = [manualForm.preparedBy, manualForm.verifiedBy, manualForm.dispatchBy];
        const sigXs = [14, 76, 138];

        sigLabels.forEach((label, i) => {
            doc.setFont(undefined, 'bold');
            doc.setFontSize(8);
            doc.text(label, sigXs[i] + sigBoxW / 2, y, { align: 'center' });
            doc.setFont(undefined, 'normal');
            doc.text(sigValues[i] || '', sigXs[i] + sigBoxW / 2, y + 14, { align: 'center' });
            doc.line(sigXs[i], y + 15, sigXs[i] + sigBoxW, y + 15);
            doc.setFontSize(7);
            doc.setTextColor(100);
            if (i === 2) doc.text('Signature over Printed Name/Date', sigXs[i] + sigBoxW / 2, y + 19, { align: 'center' });
            doc.setTextColor(0);
        });

        y += 24;

        doc.setFont(undefined, 'bold');
        doc.setFontSize(9);
        doc.text('CUSTOMERS ACKNOWLEDGMENT:', 14, y);
        y += 6;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        doc.text('Customer Representative :', 14, y);
        doc.line(65, y + 1, 140, y + 1);
        doc.setTextColor(100);
        doc.setFontSize(7);
        doc.text('Signature over Printed Name/Date', 145, y);
        doc.setTextColor(0);
        y += 7;

        doc.setFontSize(8);
        doc.text('Status of Delivered Items :', 14, y);
        doc.line(65, y + 1, 140, y + 1);
        y += 10;

        doc.setFontSize(7);
        doc.setTextColor(80);
        doc.text('2-Customer Copy/1-Logistics Copy/-1File Copy', 14, y);
        doc.text(`Generated on ${new Date().toLocaleString()}`, pageW - 14, y, { align: 'right' });

        doc.save(`Gatepass_Manual_${manualForm.wbRef || new Date().getTime()}.pdf`);
    };

    const totalPages = Math.ceil(filteredMRs.length / itemsPerPage);
    const startIdx = (currentPage - 1) * itemsPerPage;
    const currentMRs = filteredMRs.slice(startIdx, startIdx + itemsPerPage);

    const formattedDate = selectedMR
        ? new Date(selectedMR.mrDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
        : '';

    const GatepassDocument = () => (
        <div ref={printRef} style={{ width: '794px', minHeight: '1123px', padding: '30px 40px', fontFamily: 'Arial, sans-serif', fontSize: '11px', color: '#000', background: '#fff', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '6px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px' }}>MEGA SOFT HYGIENIC PRODUCT INC.</div>
                <div style={{ fontSize: '10px', marginTop: '2px' }}>38 - E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City</div>
                <div style={{ fontSize: '10px', marginTop: '1px' }}>Telefax : 984-10-01</div>
                <div style={{ fontWeight: 'bold', fontSize: '13px', marginTop: '4px', letterSpacing: '2px' }}>GATEPASS</div>
            </div>
            <hr style={{ border: 'none', borderTop: '1.5px solid #000', margin: '6px 0 10px 0' }} />

            {/* ── GI Number Badge ── */}
            {getGINumber(selectedMR) !== '—' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '3px 10px', fontSize: '10px' }}>
                        <span style={{ fontWeight: 'bold', color: '#1e40af' }}>GI No.: </span>
                        <span style={{ fontWeight: 'bold', color: '#1e3a8a', fontFamily: 'monospace' }}>{getGINumber(selectedMR)}</span>
                    </div>
                </div>
            )}

            <div style={{ marginBottom: '4px' }}><span style={{ fontWeight: 'bold', fontSize: '12px' }}>DELIVERY TO</span></div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0' }}>
                <tbody>
                    <tr>
                        <td style={{ border: 'none', padding: '2px 4px', width: '110px', fontWeight: 'bold', fontSize: '10px' }}>CUSTOMER NAME</td>
                        <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
                        <td style={{ padding: '2px 4px', borderBottom: '1px solid #000', fontSize: '10px', width: '320px', fontWeight: 'bold' }}>{selectedMR?.branchSpecify || selectedMR?.departmentName || ''}</td>
                        <td style={{ border: 'none', padding: '2px 12px', fontWeight: 'bold', fontSize: '10px', textAlign: 'right', width: '80px' }}>WBREF:</td>
                        <td style={{ padding: '2px 4px', borderBottom: '1px solid #000', fontSize: '10px', minWidth: '120px' }}>{selectedMR?.gI_Number || ''}</td>
                    </tr>
                    <tr>
                        <td style={{ border: 'none', padding: '2px 4px', fontWeight: 'bold', fontSize: '10px' }}>CUSTOMER ADDRESS</td>
                        <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
                        <td colSpan={3} style={{ borderBottom: '1px solid #000', padding: '2px 4px', fontSize: '10px' }}>&nbsp;</td>
                    </tr>
                </tbody>
            </table>
            <div style={{ marginTop: '8px', marginBottom: '2px' }}><span style={{ fontWeight: 'bold', fontSize: '12px' }}>DELIVERY INSTRUCTION</span></div>
            <table style={{ width: '60%', borderCollapse: 'collapse', marginBottom: '4px' }}>
                <tbody>
                    {[{ label: 'DELIVERY DATE', value: formattedDate }, { label: 'EXPECTED ARRIVAL DATE', value: '' }, { label: 'TRUCK PLATE NO.', value: '' }].map((row, i) => (
                        <tr key={i}>
                            <td style={{ border: 'none', padding: '2px 4px', fontWeight: 'bold', fontSize: '10px', width: '160px' }}>{row.label}</td>
                            <td style={{ borderBottom: '1px solid #000', padding: '2px 8px', fontSize: '10px', minWidth: '160px' }}>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div style={{ marginTop: '4px', marginBottom: '10px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '10px' }}>Request by: </span>
                <span style={{ display: 'inline-block', borderBottom: '1px solid #000', minWidth: '200px', fontSize: '10px', paddingLeft: '8px' }}>{selectedMR?.requestedByName || ''}</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {['CODE', 'ITEM DESCRIPTION', 'REMARKS', 'QTY', 'PACKAGING'].map((h, i) => (
                            <th key={i} style={{ border: '1px solid #000', padding: '5px 8px', textAlign: 'center', fontWeight: 'bold', fontSize: '10px', width: ['10%', '38%', '24%', '10%', '18%'][i] }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {selectedMRDetails.length === 0 ? (
                        <tr><td colSpan={5} style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', color: '#999' }}>No items found</td></tr>
                    ) : selectedMRDetails.map((item, idx) => (
                        <tr key={idx}>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px' }}>{item.itemCode || ''}</td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'left', fontSize: '10px', textTransform: 'uppercase' }}>{item.itemDescription || item.itemCode}</td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px' }}>{item.remarks || item.purpose || ''}</td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.requestedQty}</td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.uom?.toUpperCase() || ''}</td>
                        </tr>
                    ))}
                    {Array.from({ length: Math.max(0, 5 - selectedMRDetails.length) }).map((_, i) => (
                        <tr key={`empty-${i}`}>
                            {[...Array(5)].map((_, j) => <td key={j} style={{ border: '1px solid #000', padding: '4px 8px', height: '22px' }}>&nbsp;</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div style={{ height: '24px' }} />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr>
                        {[{ label: 'Prepared by:', name: selectedMR?.requestedByName }, { label: 'Verified By:', name: selectedMR?.approvedByName }, { label: 'Dispatch by:', name: selectedMR?.releasedByName }].map((sig, i) => (
                            <td key={i} style={{ width: '33%', padding: i === 0 ? '0 20px 0 0' : i === 1 ? '0 10px' : '0 0 0 20px', verticalAlign: 'top', border: 'none' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '28px' }}>{sig.label}</div>
                                    <div style={{ borderBottom: '1px solid #000', paddingBottom: '2px', fontSize: '11px', fontWeight: 'bold', minHeight: '20px' }}>{sig.name || ''}</div>
                                    {i === 2 && <div style={{ fontSize: '9px', color: '#555', marginTop: '2px' }}>Signature over Printed Name/Date</div>}
                                </div>
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
            <div style={{ marginTop: '20px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>CUSTOMERS ACKNOWLEDGMENT:</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            <td style={{ border: 'none', padding: '2px 4px', width: '180px', fontSize: '10px' }}>Customer Representative</td>
                            <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
                            <td style={{ borderBottom: '1px solid #000', padding: '2px 8px', fontSize: '10px', width: '260px' }}>&nbsp;</td>
                            <td style={{ border: 'none', padding: '2px 8px', textAlign: 'center', fontSize: '9px', color: '#555', width: '220px' }}>Signature over Printed Name/Date</td>
                        </tr>
                        <tr>
                            <td style={{ border: 'none', padding: '4px 4px', fontSize: '10px' }}>Status of Delivered Items</td>
                            <td style={{ border: 'none', padding: '4px 4px', fontSize: '10px' }}>:</td>
                            <td style={{ borderBottom: '1px solid #000', padding: '4px 8px', fontSize: '10px' }}>&nbsp;</td>
                            <td style={{ border: 'none' }}>&nbsp;</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: '18px', fontSize: '9px', color: '#333' }}>2-Customer Copy/1-Logistics Copy/-1File Copy</div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 p-6">
            <div className="max-w-8xl mx-auto">

                {/* ── PAGE HEADER ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-7 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-4 rounded-xl shadow-lg shadow-blue-200">
                                <FileText className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Gatepass</h1>
                                <p className="text-slate-500 mt-1 text-sm">Branch Material Requests — Gatepass Documents</p>
                            </div>
                        </div>
                        <button onClick={fetchBranchMRs} className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-all">
                            <RefreshCw className="w-4 h-4" /> Refresh
                        </button>
                    </div>
                </div>

                {/* ── TABS ── */}
                <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setActiveTab('list')}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-sm ${
                            activeTab === 'list'
                                ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-blue-200 shadow-md'
                                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                        <FileText className="w-4 h-4" />
                        Branch MR List
                    </button>
                    <button
                        onClick={() => setActiveTab('create')}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-sm ${
                            activeTab === 'create'
                                ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-blue-200 shadow-md'
                                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                        <FilePlus className="w-4 h-4" />
                        Manual Create Gatepass
                    </button>
                </div>

                {/* ══════════════════════════════════════
                    TAB 1: BRANCH MR LIST
                ══════════════════════════════════════ */}
                {activeTab === 'list' && (
                    <>
                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {[
                                { label: 'Total Branch MRs', value: branchMRs.length, color: 'text-blue-700', bg: 'bg-blue-50' },
                                { label: 'Draft', value: branchMRs.filter(m => m.status === 0).length, color: 'text-gray-700', bg: 'bg-gray-50' },
                                { label: 'For Approval', value: branchMRs.filter(m => m.status === 1).length, color: 'text-yellow-700', bg: 'bg-yellow-50' },
                                { label: 'Approved / Released', value: branchMRs.filter(m => m.status >= 2).length, color: 'text-green-700', bg: 'bg-green-50' },
                            ].map((stat, i) => (
                                <div key={i} className={`${stat.bg} rounded-xl p-5 border border-white shadow-sm`}>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{stat.label}</p>
                                    <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* List Card */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="p-5 border-b border-slate-100 bg-slate-50">
                                <div className="relative max-w-md">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search MR No, Control No, GI No, Branch..."
                                        value={searchText}
                                        onChange={e => setSearchText(e.target.value)}
                                        className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                                    />
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex items-center justify-center py-24">
                                    <div className="text-center">
                                        <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
                                        <p className="text-slate-500 text-sm font-medium">Loading Branch Gatepasses...</p>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="flex items-center justify-center py-24">
                                    <div className="text-center">
                                        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                                        <p className="text-slate-700 font-semibold">{error}</p>
                                        <button onClick={fetchBranchMRs} className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-all">Retry</button>
                                    </div>
                                </div>
                            ) : currentMRs.length === 0 ? (
                                <div className="flex items-center justify-center py-24">
                                    <div className="text-center">
                                        <Building2 className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                                        <p className="text-slate-500 font-semibold">No Branch Gatepasses found</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-blue-700 to-blue-800 text-white text-xs uppercase tracking-wider">
                                                {['MR No.', 'GI No.', 'Control No.', 'Date', 'Branch', 'Requested By', 'Status', 'Action'].map((h, i) => (
                                                    <th key={i} className={`px-5 py-4 font-bold ${i === 7 ? 'text-center' : 'text-left'}`}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {currentMRs.map((mr, idx) => {
                                                const status = statusLabels[mr.status] || statusLabels[0];
                                                const giNo = getGINumber(mr);
                                                return (
                                                    <tr key={mr.id} className={`hover:bg-blue-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                                                        <td className="px-5 py-3.5">
                                                            <span className="font-mono font-bold text-blue-900 text-sm">{mr.mrNo}</span>
                                                        </td>
                                                                <td className="px-5 py-3.5">
                                                            <span className="font-mono font-bold text-blue-900 text-sm">{mr.gI_Number }</span>
                                                        </td>
                                                   
                                                        <td className="px-5 py-3.5">
                                                            <span className="font-mono text-slate-600 text-sm">{mr.controlNo || '—'}</span>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-sm text-slate-600">
                                                            {new Date(mr.mrDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                                                        </td>
                                                        <td className="px-5 py-3.5">
                                                            <div className="flex items-center gap-2">
                                                                <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                                <span className="text-sm font-semibold text-slate-800">{mr.branchSpecify || mr.departmentName || 'Branch'}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-sm text-slate-700">{mr.requestedByName}</td>
                                                        <td className="px-5 py-3.5">
                                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${status.color}`}>{status.label}</span>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-center">
                                                            <button onClick={() => handleViewGatepass(mr)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm hover:shadow-md">
                                                                <Printer className="w-3.5 h-3.5" /> View Gatepass
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {totalPages > 1 && (
                                <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                                    <p className="text-sm text-slate-500">Showing {startIdx + 1}–{Math.min(startIdx + itemsPerPage, filteredMRs.length)} of {filteredMRs.length}</p>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100 transition-all"><ChevronLeft className="w-4 h-4" /></button>
                                        <span className="text-sm font-semibold px-2">{currentPage} / {totalPages}</span>
                                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100 transition-all"><ChevronRight className="w-4 h-4" /></button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* ══════════════════════════════════════
                    TAB 2: MANUAL CREATE GATEPASS
                ══════════════════════════════════════ */}
                {activeTab === 'create' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                            <FilePlus className="w-5 h-5 text-blue-700" />
                            Manual Gatepass
                        </h2>

                        {/* ── DELIVERY TO ── */}
                        <section className="mb-6">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Delivery To</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Customer Name</label>
                                    <input name="customerName" value={manualForm.customerName} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Customer / Branch name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Customer Address</label>
                                    <input name="customerAddress" value={manualForm.customerAddress} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Address" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">WBREF</label>
                                    <input name="wbRef" value={manualForm.wbRef} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="e.g. WB-2025-001" />
                                </div>
                            </div>
                        </section>

                        {/* ── DELIVERY INSTRUCTION ── */}
                        <section className="mb-6">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Delivery Instruction</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Delivery Date</label>
                                    <input type="date" name="deliveryDate" value={manualForm.deliveryDate} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Expected Arrival Date</label>
                                    <input type="date" name="expectedArrivalDate" value={manualForm.expectedArrivalDate} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Truck Plate No.</label>
                                    <select onChange={handleTruckSelect}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white" defaultValue="">
                                        <option value="">Select Truck</option>
                                        {trucks.map(t => (
                                            <option key={t.id} value={t.id}>{t.truckPlate} — {t.model}</option>
                                        ))}
                                    </select>
                                    {manualForm.truckPlateNo && (
                                        <p className="mt-1 text-xs font-semibold text-green-700">✓ {manualForm.truckPlateNo}</p>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4 max-w-sm">
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Requested By</label>
                                <input name="requestedBy" value={manualForm.requestedBy} onChange={handleManualChange}
                                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-slate-50"
                                    readOnly />
                            </div>
                        </section>

                        {/* ── ITEMS TABLE ── */}
                        <section className="mb-6">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Items</h3>
                                <button onClick={addItem}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all">
                                    <Plus className="w-3.5 h-3.5" /> Add Item
                                </button>
                            </div>
                            <div className="overflow-x-auto rounded-xl border border-slate-200">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-blue-700 to-blue-800 text-white text-xs">
                                            {['Code', 'Item Description', 'Remarks', 'Qty', 'Packaging', ''].map((h, i) => (
                                                <th key={i} className="px-4 py-3 text-left font-bold">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {manualForm.items.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-3 py-2"><input value={item.code} onChange={e => handleItemChange(idx, 'code', e.target.value)} className="w-20 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none" placeholder="Code" /></td>
                                                <td className="px-3 py-2"><input value={item.description} onChange={e => handleItemChange(idx, 'description', e.target.value)} className="w-56 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none" placeholder="Description" /></td>
                                                <td className="px-3 py-2"><input value={item.remarks} onChange={e => handleItemChange(idx, 'remarks', e.target.value)} className="w-36 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none" placeholder="Remarks" /></td>
                                                <td className="px-3 py-2"><input type="number" value={item.qty} onChange={e => handleItemChange(idx, 'qty', e.target.value)} className="w-16 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none" placeholder="0" /></td>
                                                <td className="px-3 py-2"><input value={item.packaging} onChange={e => handleItemChange(idx, 'packaging', e.target.value)} className="w-28 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none" placeholder="e.g. BOX" /></td>
                                                <td className="px-3 py-2">
                                                    {manualForm.items.length > 1 && (
                                                        <button onClick={() => removeItem(idx)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><X className="w-4 h-4" /></button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {/* ── APPROVALS ── */}
                        <section className="mb-8">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Approvals & Signatures</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[
                                    { label: 'Prepared By', name: 'preparedBy' },
                                    { label: 'Verified By', name: 'verifiedBy' },
                                    { label: 'Dispatch By', name: 'dispatchBy' },
                                ].map((sig) => (
                                    <div key={sig.name}>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1">{sig.label}</label>
                                        <select onChange={(e) => {
                                            const user = users.find(u => u.id === parseInt(e.target.value));
                                            setManualForm(prev => ({
                                                ...prev,
                                                [sig.name]: user ? `${user.firstName} ${user.lastName}` : ''
                                            }));
                                        }} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white" defaultValue="">
                                            <option value="">Select User</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                                            ))}
                                        </select>
                                        {manualForm[sig.name] && (
                                            <p className="mt-1 text-xs font-semibold text-green-700">✓ {manualForm[sig.name]}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* ── EXPORT BUTTON ── */}
                        <div className="flex justify-end">
                            <button
                                onClick={exportManualToPDF}
                                className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white rounded-xl font-bold text-sm transition-all shadow-lg hover:shadow-xl hover:scale-105"
                            >
                                <Printer className="w-5 h-5" />
                                Export to PDF
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── GATEPASS MODAL (Branch MR) ── */}
            {showGatepass && selectedMR && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
                        <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5" />
                                <div>
                                    <p className="font-bold text-lg">Gatepass — {selectedMR.mrNo}</p>
                                    <div className="flex items-center gap-3 mt-0.5">
                                        <p className="text-blue-200 text-xs">{selectedMR.branchSpecify || selectedMR.departmentName}</p>
                                        {/* ── GI Number in modal header ── */}
                                        {getGINumber(selectedMR) !== '—' && (
                                            <span className="bg-white/20 text-white text-xs font-mono font-bold px-2 py-0.5 rounded-md">
                                                GI: {getGINumber(selectedMR)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button onClick={handlePrint} className="flex items-center gap-2 px-5 py-2 bg-white text-blue-900 rounded-xl font-bold text-sm hover:bg-blue-50 transition-all shadow">
                                    <Printer className="w-4 h-4" /> Print
                                </button>
                                <button onClick={() => { setShowGatepass(false); setSelectedMR(null); setSelectedMRDetails([]); }} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
                            {loadingDetails ? (
                                <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>
                            ) : (
                                <GatepassDocument />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}